import React, { useMemo, useState } from "react";
import AppButton from "./AppButton.jsx";
import { useTheme } from "../../contexts/ThemeContext.jsx";
import {
  TABLE_FIXED_HEIGHT_PX,
  TABLE_PAGE_SIZES,
} from "./tableConfig";

const GlobalDataTable = ({
  columns,
  rows,
  rowKey,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  loading = false,
  emptyText = "No data found.",
  fixedHeight = TABLE_FIXED_HEIGHT_PX,
  tableTitle,
  tableSubtitle,
  beforeTable,
  mobileExpandedRow,
  showRowsPerPage = true,
  showPagination = true,
  onPreviousPage,
  onNextPage,
  canGoPrevious,
  canGoNext,
  pageInfo,
  ariaLabel = "Data table",
}) => {
  const { theme } = useTheme();
  const [expandedRow, setExpandedRow] = useState(null);

  const safeRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);
  const count = typeof totalCount === "number" ? totalCount : safeRows.length;
  const totalPages = Math.max(1, Math.ceil(count / rowsPerPage));
  const start = count === 0 ? 0 : page * rowsPerPage + 1;
  const end = count === 0 ? 0 : Math.min((page + 1) * rowsPerPage, count);
  const prevEnabled =
    typeof canGoPrevious === "boolean" ? canGoPrevious : page > 0;
  const nextEnabled =
    typeof canGoNext === "boolean" ? canGoNext : page < totalPages - 1;

  const toggleMobileRow = (key) => {
    setExpandedRow((prev) => (prev === key ? null : key));
  };

  return (
    <div className={`glass-surface ${theme.contentBg} ${theme.contentBorder} overflow-hidden rounded-2xl border`}>
      {(tableTitle || tableSubtitle || beforeTable) && (
        <div className="space-y-2 border-b border-indigo-200/70 px-3 py-2.5">
          {tableTitle && (
            <h2 className="text-base font-bold text-slate-900 sm:text-lg">{tableTitle}</h2>
          )}
          {tableSubtitle && <p className="text-xs text-slate-600">{tableSubtitle}</p>}
          {beforeTable}
        </div>
      )}

      <div
        className="overflow-x-auto overflow-y-auto"
        style={{
          scrollbarWidth: "thin",
          maxHeight: `${fixedHeight}px`,
        }}
      >
        <table className="w-full" aria-label={ariaLabel}>
          <thead className="sticky top-0 z-10 bg-gradient-to-r from-indigo-100/90 via-violet-100/90 to-indigo-100/90 backdrop-blur-sm">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={`px-2 py-1.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-700 ${
                    column.hideOnMobile ? "hidden md:table-cell" : ""
                  } ${column.headerClassName || ""}`}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-indigo-100/80 bg-white/70">
            {loading && (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-7 text-center text-sm text-slate-500"
                >
                  Loading...
                </td>
              </tr>
            )}

            {!loading && safeRows.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-7 text-center text-sm text-slate-500"
                >
                  {emptyText}
                </td>
              </tr>
            )}

            {!loading &&
              safeRows.map((row, index) => {
                const key = rowKey(row, index);

                return (
                  <React.Fragment key={key}>
                    <tr
                      className={`text-xs transition-colors md:cursor-default cursor-pointer ${
                        index % 2 === 0 ? "bg-white/80" : "bg-indigo-50/30"
                      } hover:bg-indigo-50/60`}
                      onClick={() => {
                        if (
                          mobileExpandedRow &&
                          typeof window !== "undefined" &&
                          window.innerWidth < 768
                        ) {
                          toggleMobileRow(key);
                        }
                      }}
                    >
                      {columns.map((column) => (
                        <td
                          key={`${key}-${column.key}`}
                          className={`px-1 py-1 ${
                            column.hideOnMobile ? "hidden md:table-cell" : ""
                          } ${column.cellClassName || ""}`}
                        >
                          {column.render ? column.render(row, index) : row[column.key]}
                        </td>
                      ))}
                    </tr>

                    {mobileExpandedRow && expandedRow === key && (
                      <tr className="md:hidden">
                        <td
                          colSpan={columns.length}
                          className="border-b border-indigo-100 bg-indigo-50/50 px-2 py-2"
                        >
                          {mobileExpandedRow(row, index)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
          </tbody>
        </table>
      </div>

      {showPagination && (
        <div className="flex items-center justify-between gap-2 border-t border-indigo-200/70 px-3 py-2.5">
          <div className="flex items-center space-x-1">
            {showRowsPerPage && (
              <>
                <span className="text-xs text-slate-700">Rows per page:</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => {
                    onRowsPerPageChange(parseInt(e.target.value, 10));
                  }}
                  aria-label="Rows per page"
                  className={`rounded-lg border border-indigo-200 bg-white px-1.5 py-0.5 text-xs text-slate-700 focus:outline-none focus:ring-2 ${theme.inputFocusRing}`}
                >
                  {TABLE_PAGE_SIZES.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>

          <div className="flex items-center space-x-1">
            <span className="text-xs text-slate-700" aria-live="polite">
              {pageInfo || `${start}-${end} of ${count}`}
            </span>

            <div className="flex space-x-1">
              <AppButton
                variant="clear"
                size="sm"
                onClick={() => {
                  if (onPreviousPage) {
                    onPreviousPage();
                    return;
                  }
                  onPageChange(Math.max(0, page - 1));
                }}
                disabled={!prevEnabled}
              >
                Previous
              </AppButton>
              <AppButton
                variant="clear"
                size="sm"
                onClick={() => {
                  if (onNextPage) {
                    onNextPage();
                    return;
                  }
                  onPageChange(Math.min(totalPages - 1, page + 1));
                }}
                disabled={!nextEnabled}
              >
                Next
              </AppButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalDataTable;
